/** Room kept around the pins, in picture pixels, for the pins themselves and the labels under them. */
export const MAP_PADDING_PX = 100;

/**
 * How far in the map may go to fit pins that are close together. Pins a few meters apart need more than zoom 15 (a pin
 * is wider than 50 m there, so reports within a few meters of each other were drawn on top of each other). It stops
 * at 19, which is as far as the satellite imagery is sharp: beyond that Mapbox enlarges it and the ground under the
 * pins turns blurry.
 */
export const MAX_FIT_ZOOM = 19;

/**
 * The zoom for pins that are all in one place, where there is nothing to fit: as close as the imagery is sharp
 * (see {@link MAX_FIT_ZOOM}), with the pin and its label as the picture.
 */
export const SINGLE_PLACE_ZOOM = 19;

/** Pins closer together than this (meters) are in one place. */
export const SAME_PLACE_METERS = 1;

export type ExportMapView =
  | { kind: 'bounds'; bounds: [[number, number], [number, number]]; padding: number; maxZoom: number }
  | { kind: 'center'; center: [number, number]; zoom: number };

const EARTH_RADIUS_METERS = 6_371_000;

/** The distance in meters between two positions, along the surface of the earth. */
export function distanceMeters(a: { longitude: number; latitude: number }, b: { longitude: number; latitude: number }): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const deltaLatitude = toRadians(b.latitude - a.latitude);
  const deltaLongitude = toRadians(b.longitude - a.longitude);
  const h =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(a.latitude)) * Math.cos(toRadians(b.latitude)) * Math.sin(deltaLongitude / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Where the map of an export report looks: the area the pins are in, as close as the pins allow, or, when they are
 * all in one place, that place.
 *
 * @param pins the pins to show, at least one
 */
export function exportMapView(pins: { longitude: number; latitude: number }[]): ExportMapView {
  if (pins.length === 0) {
    return { kind: 'center', center: [0, 0], zoom: 1 };
  }

  const west = Math.min(...pins.map((pin) => pin.longitude));
  const east = Math.max(...pins.map((pin) => pin.longitude));
  const south = Math.min(...pins.map((pin) => pin.latitude));
  const north = Math.max(...pins.map((pin) => pin.latitude));

  const spread = distanceMeters({ longitude: west, latitude: south }, { longitude: east, latitude: north });
  if (spread < SAME_PLACE_METERS) {
    return { kind: 'center', center: [(west + east) / 2, (south + north) / 2], zoom: SINGLE_PLACE_ZOOM };
  }

  return {
    kind: 'bounds',
    bounds: [
      [west, south],
      [east, north],
    ],
    padding: MAP_PADDING_PX,
    maxZoom: MAX_FIT_ZOOM,
  };
}
